import { takeEvery } from 'redux-saga';
import { take, fork, call, put, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';
import v from 'validator';
import _ from 'lodash';

import { reportError } from '../../utils/error-handling-service';
import { getDeliverable, updateDeliverableComments } from '../../utils/jsonapi-functions/deliverables';
import { createComment } from '../../utils/jsonapi-functions/comments';
import {
  getDeliverableComments,
  getDiscussionId,
  getDiscussionName,
  getCampaignId,
  getCampaignName,
  getDeliverableName,
  getDocumentByVersion,
  getDocumentApproved,
  getDocumentId,
  getDeliverableId,
} from '../../selectors/deliverable-selectors';
import { getUserId, getUserName } from '../../selectors/user-selectors';
import { getCampaignPermissionOrganisationId, getCampaignPermissionId, getCampaignPermissionName, getCampaignPermissionRole, getPermissionListLoading } from '../../selectors/campaign-permission-selectors';
import { updateDocumentApproved } from '../../utils/jsonapi-functions/documents';
import { SET_MY_PERMISSION_INFO } from '../../containers/campaign/constants';
import { getPermissionId } from '../../selectors/campaign-selectors';
import { trackEvent } from '../../utils/metric-analytics-service';
import { ADD_TRACKED_ASSET } from '../../containers/track-asset-modal/constants';
import { getFileType } from '../../utils/helper-functions';
import { getTemporarySignedUrl } from '../../utils/s3-service';
import { DELIVERABLE_PAGE, YOUTUBE_URL, EXTERNAL_URL } from '../../utils/static-configuration';
import { SEND_MESSAGE } from '../../containers/discussion/constants';
import { LOAD_CAMPAIGNS_SUCCESS } from '../influencers-campaign-list/constants';
import { SET_SENDBIRD_CHANNEL_URL } from '../../constants/sendbird';

import {
  LOAD_DELIVERABLE,
  LOAD_DELIVERABLE_SUCCESS,
  LOAD_DELIVERABLE_ERROR,
  CREATE_COMMENT,
  CREATE_COMMENT_SUCCESS,
  CREATE_COMMENT_ERROR,
  APPROVE_DELIVERABLE,
  UPDATE_DELIVERABLE_DOCUMENT_STATE,
  CHANGE_SELECT_DOCUMENT_VERSION,
  CHANGE_SELECT_DOCUMENT_VERSION_SUCCESS,
} from './constants';

export function* loadDeliverableData(action) {
  try {
    const deliverableData = yield call(getDeliverable, action.deliverableId);

    if (deliverableData.comments && deliverableData.comments[0]) {
      deliverableData.comments.reverse();
    } else {
      deliverableData.comments = [];
    }
    let selectedDocument = fromJS({});
    const documentVersionOptions = [];
    let currentHighestVersion = 1;
    if (deliverableData.documents && deliverableData.documents[0]) {
      let latestDocument;
      const documentsMap = {};
      _.forEach(deliverableData.documents, document => {
        if (document.version >= currentHighestVersion) {
          currentHighestVersion = document.version;
          latestDocument = document;
        }
        documentsMap[document.version] = document;
        documentVersionOptions.push({
          value: document.version,
          name: `VERSION ${document.version}`,
          action: { version: document.version },
          label: `VERSION ${document.version}`,
        });
      });
      selectedDocument = yield call(setupDocument, fromJS(latestDocument));
      deliverableData.documents = documentsMap;
    } else if (deliverableData.document) {
      selectedDocument = yield call(setupDocument, fromJS(deliverableData.document));
    }
    documentVersionOptions.sort((a, b) => b.value - a.value);

    // If the permissions list isn't loaded yet then wait for it to finish
    const campaignPermissionLoading = yield select(getPermissionListLoading);
    if (campaignPermissionLoading) {
      yield take(LOAD_CAMPAIGNS_SUCCESS);
    }
    const campaignId = deliverableData.discussion.campaign.id;

    const myCampaignOrganisationId = yield select(getCampaignPermissionOrganisationId, campaignId);
    const myPermissionId = yield select(getCampaignPermissionId, campaignId);
    const myCampaignRole = yield select(getCampaignPermissionRole, campaignId);
    const myPermissionName = yield select(getCampaignPermissionName, campaignId);
    yield put({
      type: SET_MY_PERMISSION_INFO,
      role: myCampaignRole,
      id: myPermissionId,
      name: myPermissionName,
      organisationId: myCampaignOrganisationId,
    });

    const sendbirdChannelURL = deliverableData.discussion
      ? deliverableData.discussion.channelURL
      : null;
    yield put({ type: SET_SENDBIRD_CHANNEL_URL, sendbirdChannelURL });

    yield put({
      type: LOAD_DELIVERABLE_SUCCESS,
      data: fromJS(deliverableData),
      selectedDocument,
      documentVersionOptions,
      selectedVersion: currentHighestVersion,
      mostRecentDocument: selectedDocument,
    });
  } catch (error) {
    reportError('LOAD_DELIVERABLE_ERROR', error.message);
    yield put({
      type: LOAD_DELIVERABLE_ERROR,
      data: error.message,
    });
  }
}

function* changeSelectedDocumentVersion(action) {
  const { version } = action.data;

  const selectedDocument = yield select(getDocumentByVersion, version);
  const setupSelectedDocument = yield call(setupDocument, selectedDocument);

  yield put({
    type: CHANGE_SELECT_DOCUMENT_VERSION_SUCCESS,
    selectedDocument: setupSelectedDocument,
    selectedVersion: version,
  });
}

function* setupDocument(document) {
  if (!document) return null;
  const filePath = document.get('filePath');
  const fileType = document.get('fileType');
  const contentType = getFileType(filePath);

  let updatedDocument = document.set('contentType', contentType);

  if (contentType !== YOUTUBE_URL && fileType !== 'url' && fileType !== 'text') {
    // If file can be downloaded then set the downloadURL
    const downloadURL = yield call(getDownloadFile, filePath);
    if (downloadURL) {
      updatedDocument = updatedDocument.set('downloadURL', downloadURL);
    }
  } else {
    updatedDocument = updatedDocument.set('externalURL', filePath);
  }
  if (contentType !== YOUTUBE_URL && fileType !== 'upload' && fileType !== 'text') { updatedDocument = updatedDocument.set('contentType', EXTERNAL_URL); }
  return updatedDocument;
}

function* getDownloadFile(filePath) {
  try {
    const fileURL = yield call(getTemporarySignedUrl, filePath);
    return fileURL;
  } catch (error) {
    return false;
  }
}

export function* createNewComment(action) {
  try {
    const { comment, deliverableId, documentId } = action.data;
    const userId = yield select(getUserId);

    const data = {
      comment,
      deliverableId,
      documentId,
      userId,
    };

    const newComment = yield call(createComment, data);
    const immutableComment = fromJS(newComment.toJSONTree());
    const updatedDeliverable = yield call(updateDeliverableComments, deliverableId, newComment);

    const comments = yield select(getDeliverableComments);
    const updatedComments = comments.unshift(immutableComment);

    yield put({ type: CREATE_COMMENT_SUCCESS, comments: updatedComments });
  } catch (error) {
    reportError('CREATE_COMMENT_ERROR', error.message);
    yield put({ type: CREATE_COMMENT_ERROR, data: error.message });
  }
}

export function* initiateDeliverableApproved(actions) {
  try {
    const deliverableId = yield select(getDeliverableId);
    const documentId = yield select(getDocumentId);
    const approved = yield select(getDocumentApproved);
    const userName = yield select(getUserName);

    // Swap approval
    const updatedApproved = !approved;
    const approvedData = {
      approved: updatedApproved,
      approvedBy: userName,
      approvalDate: new Date(),
    };

    const newDocument = yield call(updateDocumentApproved, documentId, approvedData);
    const immutableDocument = fromJS(newDocument);

    const permissionId = yield select(getPermissionId);
    const discussionId = yield select(getDiscussionId);
    const message = 'deliverable';
    const metaData = {
      messageType: 'deliverable',
      discussionId,
      permissionId,
      docId: newDocument.id,
      deliverableId,
      deliverableStatus: approvedData.approved ? 'approved' : 'submitted',
    };

    const campaignId = yield select(getCampaignId);
    const campaignName = yield select(getCampaignName);
    const discussionName = yield select(getDiscussionName);
    const deliverableName = yield select(getDeliverableName);

    const approveDeliverableData = {
      campaignId,
      collaborationId: discussionId,
      collaborationName: discussionName,
      deliverableId,
      deliverableName,
    };
    trackEvent('Approve Deliverable', approveDeliverableData);

    const messageData = {
      message,
      metaData,
      page: DELIVERABLE_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    yield put({
      type: UPDATE_DELIVERABLE_DOCUMENT_STATE,
      document: immutableDocument,
    });
  } catch (error) {
    reportError('UPDATE_DELIVERABLE_APPROVED_ERROR', error.message);
  }
}

export function* watchInitiateDeliverableApproved() {
  yield takeEvery(APPROVE_DELIVERABLE, initiateDeliverableApproved);
}

export function* watchLoadDeliverableRequests() {
  yield takeEvery(LOAD_DELIVERABLE, loadDeliverableData);
}

export function* watchCreateComments() {
  yield takeEvery(CREATE_COMMENT, createNewComment);
}

export function* watchChangeSelectedDocumentVersion() {
  yield takeEvery(CHANGE_SELECT_DOCUMENT_VERSION, changeSelectedDocumentVersion);
}

export default function* deliverableSagas() {
  yield [
    fork(watchLoadDeliverableRequests),
    fork(watchCreateComments),
    fork(watchInitiateDeliverableApproved),
    fork(watchChangeSelectedDocumentVersion),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/deliverable/sagas.js
 **/